/**
 * Varano Construction — live site config (photos only).
 *
 * The admin CMS for this site is intentionally light: admins swap *imagery*
 * only. All copy lives in the components. So this config carries just two
 * things:
 *   1. `images` — a fixed set of named image slots (hero, section photos,
 *      per-service photos) the admin can replace.
 *   2. `gallery` — the Projects page photo grid (add / remove / categorize).
 *
 * The public site fetches `/site-config.json` from its own origin at runtime
 * (CloudFront serves it from the CMS config bucket with a 60s TTL), so admin
 * changes appear within ~1 minute. `defaultConfig` is the fallback baked into
 * the bundle for first paint and for when the network is unavailable.
 */

export type ProjectCategory =
  | "Additions"
  | "Kitchens"
  | "Bathrooms"
  | "Decks"
  | "Windows & Doors"
  | "Custom Carpentry";

export const PROJECT_CATEGORIES: ProjectCategory[] = [
  "Additions",
  "Kitchens",
  "Bathrooms",
  "Decks",
  "Windows & Doors",
  "Custom Carpentry",
];

export interface GalleryItem {
  id: string;
  url: string;
  alt: string;
  category: ProjectCategory;
  /** Sort order within the grid (ascending). */
  order: number;
  /** Hidden from the public grid when false (admin can stage without showing). */
  active: boolean;
}

/** The fixed set of replaceable image slots on the marketing site. */
export type ImageSlot =
  | "heroHome"
  | "homeWelcome"
  | "aboutMain"
  | "serviceAdditions"
  | "serviceKitchen"
  | "serviceBathroom"
  | "serviceDecks"
  | "serviceWindowsDoors"
  | "serviceCustomCarpentry";

/** Display metadata for each slot — drives the CMS Photos tab and alt text. */
export const IMAGE_SLOTS: {
  key: ImageSlot;
  label: string;
  hint: string;
}[] = [
  { key: "heroHome", label: "Homepage hero", hint: "Wide shot behind the homepage headline. ~1600×1000." },
  { key: "homeWelcome", label: "Homepage “Welcome” photo", hint: "The photo beside the welcome paragraph on the homepage." },
  { key: "aboutMain", label: "About page photo", hint: "The main photo on the About page." },
  { key: "serviceAdditions", label: "Service — Home Additions", hint: "Card photo on the Services page." },
  { key: "serviceKitchen", label: "Service — Kitchen Remodeling", hint: "Card photo on the Services page." },
  { key: "serviceBathroom", label: "Service — Bathroom Remodeling", hint: "Card photo on the Services page." },
  { key: "serviceDecks", label: "Service — Decks", hint: "Card photo on the Services page." },
  { key: "serviceWindowsDoors", label: "Service — Windows & Doors", hint: "Card photo on the Services page." },
  { key: "serviceCustomCarpentry", label: "Service — Custom Carpentry", hint: "Card photo on the Services page." },
];

export interface SiteConfig {
  _version: number;
  _updatedAt: string;
  _updatedBy: string;
  /** Accent ("secondary") color as a hex string (e.g. "#c4923f"). The primary
   *  color is locked to the logo and is not configurable. */
  secondaryColor: string;
  images: Record<ImageSlot, string>;
  gallery: GalleryItem[];
}

/** Default accent color until the client picks one. */
export const DEFAULT_SECONDARY_COLOR = "#c4923f";

export const DEFAULT_IMAGES: Record<ImageSlot, string> = {
  heroHome: "/images/defaults/hero.svg",
  homeWelcome: "/images/defaults/welcome.svg",
  aboutMain: "/images/defaults/about.svg",
  serviceAdditions: "/images/defaults/additions.svg",
  serviceKitchen: "/images/defaults/kitchen.svg",
  serviceBathroom: "/images/defaults/bathroom.svg",
  serviceDecks: "/images/defaults/decks.svg",
  serviceWindowsDoors: "/images/defaults/windows-doors.svg",
  serviceCustomCarpentry: "/images/defaults/custom-carpentry.svg",
};

export const defaultConfig: SiteConfig = {
  _version: 1,
  _updatedAt: "2026-06-02T00:00:00Z",
  _updatedBy: "system",
  secondaryColor: DEFAULT_SECONDARY_COLOR,
  images: { ...DEFAULT_IMAGES },
  gallery: [
    { id: "seed-additions", url: "/images/defaults/gallery-additions.svg", alt: "Home addition", category: "Additions", order: 0, active: true },
    { id: "seed-kitchen", url: "/images/defaults/gallery-kitchen.svg", alt: "Kitchen remodel", category: "Kitchens", order: 1, active: true },
    { id: "seed-bathroom", url: "/images/defaults/gallery-bathroom.svg", alt: "Bathroom remodel", category: "Bathrooms", order: 2, active: true },
    { id: "seed-decks", url: "/images/defaults/gallery-decks.svg", alt: "Custom deck", category: "Decks", order: 3, active: true },
    { id: "seed-windows-doors", url: "/images/defaults/gallery-windows-doors.svg", alt: "Windows and doors", category: "Windows & Doors", order: 4, active: true },
    { id: "seed-carpentry", url: "/images/defaults/gallery-custom-carpentry.svg", alt: "Custom carpentry", category: "Custom Carpentry", order: 5, active: true },
  ],
};

/**
 * Allow only safe URL forms for values that get fed into `<img src>` / `<a href>`.
 * Returns "" for anything that isn't an http(s) URL or a root-relative path —
 * defense-in-depth, since a compromised admin token can write arbitrary JSON
 * to the CMS config but must not be able to inject `javascript:`/`data:` URLs
 * into the public site.
 */
function safeUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (trimmed.startsWith("/")) return trimmed;
  if (/^https?:/i.test(trimmed)) return trimmed;
  return "";
}

/** Accept only a `#rgb` / `#rrggbb` hex color; "" otherwise (falls back to default). */
function safeColor(value: unknown): string {
  if (typeof value !== "string") return "";
  const t = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(t) ? t : "";
}

function isProjectCategory(value: unknown): value is ProjectCategory {
  return typeof value === "string" && (PROJECT_CATEGORIES as string[]).includes(value);
}

/**
 * Deep-merge a (possibly partial / stale) stored config onto the defaults so
 * new slots stay safe on older saved config, and unsafe/empty URLs fall back
 * to the baked-in placeholder.
 */
export function normalizeSiteConfig(
  config: Partial<SiteConfig> | null | undefined,
): SiteConfig {
  const incoming = config ?? {};

  const images = {} as Record<ImageSlot, string>;
  for (const { key } of IMAGE_SLOTS) {
    images[key] = safeUrl(incoming.images?.[key]) || DEFAULT_IMAGES[key];
  }

  const gallery: GalleryItem[] = Array.isArray(incoming.gallery)
    ? incoming.gallery
        .map((item, i): GalleryItem | null => {
          const url = safeUrl(item?.url);
          if (!url) return null;
          return {
            id: typeof item?.id === "string" && item.id ? item.id : `item-${i}`,
            url,
            alt: typeof item?.alt === "string" ? item.alt : "",
            category: isProjectCategory(item?.category) ? item.category : "Kitchens",
            order: typeof item?.order === "number" ? item.order : i,
            active: item?.active !== false,
          };
        })
        .filter((x): x is GalleryItem => x !== null)
    : defaultConfig.gallery.map((g) => ({ ...g }));

  return {
    _version: typeof incoming._version === "number" ? incoming._version : defaultConfig._version,
    _updatedAt: typeof incoming._updatedAt === "string" ? incoming._updatedAt : defaultConfig._updatedAt,
    _updatedBy: typeof incoming._updatedBy === "string" ? incoming._updatedBy : defaultConfig._updatedBy,
    secondaryColor: safeColor(incoming.secondaryColor) || defaultConfig.secondaryColor,
    images,
    gallery,
  };
}
